import React, { useState, useEffect } from 'react';
import SubsectionTitle from './SubsectionTitle';

export default function DeliveryMethodSelector({ settings, cartData, setCartAttributes, cartAttributes }) {
	const [cartTotal, setCartTotal] = useState(0);
	const [minOrderAmount, setMinOrderAmount] = useState(0);

	// Calculate cart total and min order amount
	useEffect(() => {
		if (cartData && cartData.items) {
			const totalPhysicalProducts = cartData.items
				.filter((item) => item.requires_shipping)
				.reduce((acc, item) => acc + item.price * item.quantity, 0);
			setCartTotal(totalPhysicalProducts / 100);
		}

		if (settings?.minOrder) {
			// console.log('settings.currency', settings.currency);
			if (settings.currency === 'EUR') {
				setMinOrderAmount(parseInt(settings.minOrderInEuro));
			} else {
				setMinOrderAmount(parseInt(settings.minOrder));
			}
		}
	}, [cartData]);

	const isDeliveryEnabled = cartTotal && minOrderAmount && cartTotal >= minOrderAmount;

	// Only force pickup if delivery becomes unavailable (cartTotal < minOrderAmount)
	// This should only happen when cart changes, not when timeslots change
	useEffect(() => {
		// Only check if we have all required data
		if (!cartData || !cartData.items || cartData.items.length === 0 || minOrderAmount === 0) {
			return;
		}

		// Only force pickup if delivery is disabled AND current method is delivery
		// Use functional update to get latest state without depending on cartAttributes
		if (isDeliveryEnabled === false) {
			setCartAttributes((prev) => {
				// Only switch if currently set to delivery
				if (prev.deliveryMethod === 'delivery') {
					//console.log('Force pickup because delivery is disabled from DeliveryMethodSelector');
					return {
						...prev,
						deliveryMethod: 'pickup',
						desiredTime: null, // Clear delivery time when switching to pickup
						intervalUnavailable: null,
					};
				}
				
				return prev; // No change needed
			});
		}
	}, [isDeliveryEnabled, cartData, minOrderAmount]);

	const handleDeliveryMethodChange = (method) => {
		setCartAttributes((prev) => ({
			...prev,
			deliveryMethod: method,
			pickupLocation: method === 'delivery' ? null : prev.pickupLocation,
			// Clear delivery-specific fields when switching to pickup
			...(method === 'pickup'
				? {
						desiredTime: null,
						intervalUnavailable: null,
				  }
				: {}),
		}));
	};

	if (cartAttributes.deliveryMethod === 'digitalProductsOnly') {
		return null;
	}

	return (
		<div className="delivery-method-selector">
			<SubsectionTitle settings={settings} stepNumber={1} injectAbove={true} cartAttributes={cartAttributes} />

			{settings.buttonDeliveryNotice && (
				<div className="delivery-method-notice">
					<p className="h5">
						{settings.buttonDeliveryNotice.replace(
							'[minAmountAndCurrency]',
							`${minOrderAmount} ${settings.currency}`
						)}
					</p>
				</div>
			)}

			<div className="delivery-method-buttons">
				<button
					className={`delivery-method-button button button--lg ${
						cartAttributes.deliveryMethod === 'pickup' ? 'button--active' : ''
					}`}
					onClick={() => handleDeliveryMethodChange('pickup')}
				>
					{settings.buttonPickupLabel}
				</button>

				<button
					className={`delivery-method-button button button--lg ${
						cartAttributes.deliveryMethod === 'delivery' ? 'button--active' : ''
					} ${!isDeliveryEnabled ? 'button--disabled' : ''}`}
					onClick={() => isDeliveryEnabled && handleDeliveryMethodChange('delivery')}
					disabled={!isDeliveryEnabled}
				>
					{settings.buttonDeliveryLabel}
				</button>
			</div>
		</div>
	);
}
